import { useT } from "../lib/i18n.tsx";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useAltezzaDisponibile } from "../lib/altezza.ts";
import { useQuery } from "../lib/hooks.ts";
import { livelloDi, segmentaAnsi, type Livello, type Segmento } from "../lib/log-ansi.ts";
import { streamEvents } from "../lib/sse.ts";
import { STILE_LIVELLO, TestoAnsi } from "./TestoLog.tsx";
import { Badge, Button, Card, Empty, ErrorNotice, Spinner, cx } from "./ui.tsx";

/**
 * Container di uno stack e i loro log, seguiti dal vivo.
 *
 * Risponde alla domanda che finora richiedeva `docker ps` e `docker logs` sulla
 * macchina: cosa sta girando, e cosa sta dicendo.
 */

interface ContainerStack {
  id: string;
  name: string;
  service: string | null;
  image: string;
  state: string;
  status: string;
}

interface RigaLog {
  container: string;
  stream: "stdout" | "stderr";
  text: string;
}

/** La riga come la si mostra: già spezzata in segmenti e col suo livello. */
interface RigaLetta {
  stream: "stdout" | "stderr";
  segmenti: Segmento[];
  /** Il testo senza sequenze ANSI, in minuscolo: è ciò su cui si cerca. */
  cercabile: string;
  livello: Livello | null;
}

/** Filtri: ogni voce mostra le righe di quel livello e di quelli più gravi. */
const FILTRI = [
  { id: "tutti", etichetta: "container.filtroTutti", livelli: null },
  { id: "avvisi", etichetta: "container.filtroAvvisi", livelli: ["errore", "avviso"] },
  { id: "errori", etichetta: "container.filtroErrori", livelli: ["errore"] },
] as const;

function leggi(riga: RigaLog, precedente: RigaLetta | undefined): RigaLetta {
  const segmenti = segmentaAnsi(riga.text);
  const testo = segmenti.map((s) => s.text).join("");
  // Le righe rientrate senza livello proprio (stack trace, JSON su più righe)
  // continuano quella sopra: un errore resta un errore fino in fondo.
  const livello = livelloDi(testo) ?? (/^\s/.test(testo) ? (precedente?.livello ?? null) : null);
  return { stream: riga.stream, segmenti, cercabile: testo.toLowerCase(), livello };
}

// ponytail: tetto fisso alle righe tenute in memoria, una finestra virtuale se mai servisse di più
const MAX_RIGHE = 2000;

/**
 * Quando le righe ricevute compaiono a schermo. Il flusso resta sempre aperto:
 * fermare il riquadro non deve far perdere righe, né rileggerle due volte.
 */
const AGGIORNAMENTI = [
  { id: "live", etichetta: "container.live", secondi: 0 },
  { id: "5s", etichetta: "container.ogni5", secondi: 5 },
  { id: "10s", etichetta: "container.ogni10", secondi: 10 },
  { id: "30s", etichetta: "container.ogni30", secondi: 30 },
  { id: "1m", etichetta: "container.ogniMinuto", secondi: 60 },
  { id: "pausa", etichetta: "container.inPausa", secondi: null },
] as const;
type Aggiornamento = (typeof AGGIORNAMENTI)[number];

/** Aggiunge le righe arrivate a quelle mostrate, nell'ordine e col tetto. */
export function accoda(mostrate: RigaLetta[], arrivate: RigaLog[]): RigaLetta[] {
  const tutte = [...mostrate];
  for (const riga of arrivate) tutte.push(leggi(riga, tutte.at(-1)));
  return tutte.slice(-MAX_RIGHE);
}

export function SchedaContainer({ stackId }: { stackId: number }) {
  const elenco = useQuery<{ containers: ContainerStack[] }>(`/stacks/${stackId}/containers`);
  const [scelto, setScelto] = useState<string | null>(null);
  const t = useT();

  const containers = elenco.data?.containers ?? [];
  // Senza una scelta esplicita si apre il primo in esecuzione: aprire la scheda
  // e trovare il riquadro dei log vuoto costringe a un clic in più ogni volta.
  const attivo = scelto ?? (containers.find((c) => c.state === "running") ?? containers[0])?.id ?? null;

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(16rem,22rem)_minmax(0,1fr)]">
      <section className="min-w-0 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold">{t.t("container.titolo")}</h2>
          <Button className="text-xs" loading={elenco.loading} onClick={elenco.reload}>
            {t.t("container.aggiorna")}
          </Button>
        </div>

        <ErrorNotice error={elenco.error} />

        {elenco.data && containers.length === 0 && (
          <Empty title={t.t("container.vuoto")} hint={t.t("container.vuotoAiuto")} />
        )}

        {containers.length > 0 && (
          <Card className="divide-y overflow-hidden">
            {containers.map((c) => (
              <button
                key={c.id}
                onClick={() => setScelto(c.id)}
                className={cx(
                  "flex w-full flex-col gap-1 px-3.5 py-2.5 text-left text-sm hover:bg-black/5 dark:hover:bg-white/5",
                  attivo === c.id && "bg-black/5 dark:bg-white/5",
                )}
              >
                <span className="flex w-full items-center gap-2">
                  <Badge tone={c.state === "running" ? "success" : c.state === "exited" ? "danger" : "warning"}>
                    {c.state}
                  </Badge>
                  <span className="min-w-0 flex-1 truncate font-mono">{c.service ?? c.name}</span>
                </span>
                <span className="w-full truncate text-xs tenue" title={[c.name, c.image].join(" · ")}>
                  {c.status || c.image}
                </span>
              </button>
            ))}
          </Card>
        )}
      </section>

      {attivo && (
        <LogContainer
          key={attivo}
          stackId={stackId}
          container={containers.find((c) => c.id === attivo)}
          containerId={attivo}
        />
      )}
    </div>
  );
}

function LogContainer({
  stackId,
  containerId,
  container,
}: {
  stackId: number;
  containerId: string;
  container: ContainerStack | undefined;
}) {
  const [righe, setRighe] = useState<RigaLetta[]>([]);
  const [filtro, setFiltro] = useState<(typeof FILTRI)[number]>(FILTRI[0]);
  const [cerca, setCerca] = useState("");
  const [errore, setErrore] = useState<unknown>(null);
  const [inAscolto, setInAscolto] = useState(true);
  const [aggiornamento, setAggiornamento] = useState<Aggiornamento>(AGGIORNAMENTI[0]);
  const [selezione, setSelezione] = useState(false);
  const [inAttesa, setInAttesa] = useState(0);
  const area = useRef<HTMLDivElement>(null);
  const t = useT();
  const altezza = useAltezzaDisponibile(area, { minima: 240 });

  /**
   * Righe ricevute e non ancora mostrate. Stanno fuori dallo stato apposta:
   * finché non si mostrano, il riquadro non si ridisegna e una selezione resta
   * dov'è.
   */
  const arrivate = useRef<RigaLog[]>([]);
  // Letta dal flusso, che vive in un effetto a parte e non deve ripartire a ogni cambio.
  const subito = useRef(true);
  subito.current = aggiornamento.secondi === 0 && !selezione;

  const mostra = () => {
    if (arrivate.current.length === 0) return;
    const blocco = arrivate.current;
    arrivate.current = [];
    setInAttesa(0);
    setRighe((prima) => accoda(prima, blocco));
  };

  // Selezionare del testo nei log ferma il riquadro finché la selezione resta.
  useEffect(() => {
    const controlla = () => {
      const sel = document.getSelection();
      setSelezione(!!sel && !sel.isCollapsed && !!area.current?.contains(sel.anchorNode));
    };
    document.addEventListener("selectionchange", controlla);
    return () => document.removeEventListener("selectionchange", controlla);
  }, []);

  // Il ritmo scelto, a selezione assente: in Live si recupera subito l'arretrato.
  useEffect(() => {
    if (selezione || aggiornamento.secondi === null) return;
    if (aggiornamento.secondi === 0) return mostra();
    const timer = setInterval(mostra, aggiornamento.secondi * 1000);
    return () => clearInterval(timer);
  }, [aggiornamento, selezione]);

  /**
   * Chi legge è in fondo? Lo si registra a ogni scorrimento dell'utente, non
   * dopo l'arrivo delle righe: misurato dopo, un blocco di righe più alto della
   * soglia sembrerebbe uno scorrimento verso l'alto e il riquadro smetterebbe
   * di seguire il flusso.
   */
  const inFondo = useRef(true);
  useEffect(() => {
    const controller = new AbortController();
    const percorso = `/stacks/${stackId}/logs?container=${encodeURIComponent(containerId)}&follow=true&tail=500`;

    void (async () => {
      try {
        for await (const { data } of streamEvents<RigaLog>(percorso, {
          method: "GET",
          signal: controller.signal,
        })) {
          // L'arretrato ha lo stesso tetto delle righe mostrate: oltre, verrebbe scartato comunque.
          arrivate.current = [...arrivate.current, data].slice(-MAX_RIGHE);
          if (subito.current) mostra();
          else setInAttesa(arrivate.current.length);
        }
      } catch (causa) {
        if (!controller.signal.aborted) setErrore(causa);
      } finally {
        if (!controller.signal.aborted) setInAscolto(false);
      }
    })();

    return () => controller.abort();
  }, [stackId, containerId]);

  // Prima del disegno, così la riga nuova non compare per un istante fuori vista.
  // `scrollTop` e non `scrollIntoView`: quest'ultimo scorrerebbe anche la pagina.
  useLayoutEffect(() => {
    const el = area.current;
    if (el && inFondo.current) el.scrollTop = el.scrollHeight;
  }, [righe, filtro, cerca]);

  const ago = cerca.trim().toLowerCase();
  const visibili = righe.filter(
    (r) =>
      (!filtro.livelli || (r.livello && (filtro.livelli as readonly Livello[]).includes(r.livello))) &&
      (!ago || r.cercabile.includes(ago)),
  );
  const conta = (f: (typeof FILTRI)[number]) =>
    f.livelli ? righe.filter((r) => r.livello && (f.livelli as readonly Livello[]).includes(r.livello)).length : righe.length;

  return (
    <section className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <h2 className="truncate font-semibold">{t.t("container.logDi", { nome: container?.name ?? containerId.slice(0, 12) })}</h2>
        {inAttesa > 0 ? (
          <button onClick={mostra} className="text-xs underline decoration-dotted tenue" title={t.t("container.mostraArrivate")}>
            {t.t("container.righeNuove", { n: inAttesa })}
            {selezione && ` · ${t.t("container.selezione")}`}
          </button>
        ) : inAscolto ? (
          <span className="inline-flex items-center gap-1.5 text-xs tenue">
            <Spinner className="size-3" /> {t.t("container.inAscolto")}
          </span>
        ) : (
          <span className="text-xs tenue">{t.t("container.concluso")}</span>
        )}
        <select
          value={aggiornamento.id}
          onChange={(e) => setAggiornamento(AGGIORNAMENTI.find((a) => a.id === e.target.value) ?? AGGIORNAMENTI[0])}
          aria-label={t.t("container.aggiornamento")}
          className="rounded-md border bg-transparent px-1.5 py-0.5 text-xs"
        >
          {AGGIORNAMENTI.map((a) => (
            <option key={a.id} value={a.id}>
              {t.t(a.etichetta)}
            </option>
          ))}
        </select>
        <input
          type="search"
          value={cerca}
          onChange={(e) => setCerca(e.target.value)}
          placeholder={t.t("container.cercaSegnaposto")}
          aria-label={t.t("container.cerca")}
          className="ml-auto w-44 rounded-md border bg-transparent px-2 py-0.5 text-xs"
        />
        <div className="flex gap-1">
          {FILTRI.map((f) => (
            <button
              key={f.id}
              onClick={() => setFiltro(f)}
              className={cx(
                "rounded-md px-2 py-0.5 text-xs hover:bg-black/5 dark:hover:bg-white/5",
                filtro.id === f.id ? "bg-black/10 font-medium dark:bg-white/10" : "tenue",
              )}
            >
              {t.t(f.etichetta)} <span className="tabular-nums opacity-60">{conta(f)}</span>
            </button>
          ))}
        </div>
      </div>

      <ErrorNotice error={errore} />

      <div
        ref={area}
        style={{ height: altezza }}
        onScroll={(evento) => {
          const el = evento.currentTarget;
          inFondo.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
        }}
        className="console corridoio-barra overflow-y-auto rounded-xl px-3 py-1.5 font-mono text-[13px] leading-relaxed"
      >
        {visibili.length === 0 && (
          <p className="tenue">{t.t(righe.length === 0 ? "container.nessunaRiga" : "container.nessunaCorrisponde")}</p>
        )}
        {visibili.map((riga, i) => (
          <div
            key={i}
            className={cx(
              "-mx-1.5 border-l-2 px-1.5 whitespace-pre-wrap break-words",
              riga.livello
                ? STILE_LIVELLO[riga.livello]
                : cx("border-transparent", riga.stream === "stderr" && "text-rose-300/80"),
            )}
          >
            <TestoAnsi segmenti={riga.segmenti} cerca={ago} />
          </div>
        ))}
      </div>
    </section>
  );
}
